import type { AxiosError } from 'axios';
import { DateTime } from 'luxon';
import { type FC, useContext, useEffect, useRef, useState } from 'react';
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import Container from 'react-bootstrap/Container';
import * as icons from 'react-bootstrap-icons';
import { FormProvider, useForm } from 'react-hook-form';
import LoadingOverlay from 'react-loading-overlay-ts';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ReservationCard } from '../Cards/ReservationVerifyCard';
import './styles/styles.css';
import { config } from '../../modules/config';
import { GuestPortalContext } from '../../modules/context/guestPortalContextV2';
import { PaymentStatus } from '../../modules/enums/status';
import {
  type ErrorEnvelop,
  useGuestAuth,
  useManualQueryRequest,
  useMutationRequest,
  useNotifications,
  useQueryRequest,
  useRequest,
} from '../../modules/hooks';
import { useTransaction } from '../../modules/hooks/useTransaction';
import type {
  CombinedRegistrationStep,
  DynamicQuestionPayload,
  Guests,
  GuestsRequest,
  PropertyRegistrationStep,
  Reservation,
  ReservationRegistrationStep,
} from '../../modules/interfaces';
import { registrationLogger } from '../../modules/logger/loggers';
import { endpoints } from '../../modules/mappers/urls';
import { getTransactionContext } from '../../modules/transaction';
import { buildRequestUrl } from '../../modules/utils/buildRequestUrl';
import { mergeRegistrationSteps } from '../../modules/utils/registrationSteps';
import { getApiErrorMessage } from '../../modules/utils/transform';
import { Icon } from '../Menu/Icon';
import {
  AgreementModal,
  IdCheckModal,
  SafetyDepositModal,
  TermsAndPrivacy,
  UserDetailsModal,
} from '../Modals/GuestPagesModalsV2';
import { ConfirmStepsWarningModal } from '../Modals/GuestPagesModalsV2/ConfirmWarningModal';
import { GuestPortalErrors } from '../Modals/GuestPagesModalsV2/constants/GuestPortalConstants';
import { SafetyDepositInformation } from '../Modals/GuestPagesModalsV2/SafetyDepositInformation';
import { CardForPayPackagesModal } from '../Modals/PackagesModals/CardForPayPackagesModalV2';
import { useGetAccessCode } from './endpoints/GuestPageEnpoints';

interface GuestRegistrationProps {
  reservationId?: string;
}

type FormValues = {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  birthDate: string;
  dynamicQuestions: DynamicQuestionPayload[];
};

interface Option {
  name: string;
}

export const GuestRegistration: FC<GuestRegistrationProps> = () => {
  const ICON_SIZE = 28;
  const navigate = useNavigate();

  const [userDetailsModal, setUserDetailsModal] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [idCheckModal, setIdCheckModal] = useState(false);
  const [agreementModal, setAgreementModal] = useState(false);
  const [safetyDepositModal, setSafetyDepositModal] = useState(false);
  const [safetyDepositWarningModal, setSafetyDepositWarningModal] = useState(false);
  const [termsAndPrivacy, setTermsAndPrivacy] = useState(false);
  const [showAnotherCardModal, setShowAnotherCardModal] = useState(false);

  const [safetyDepositInformation, setSafetyDepositInformation] = useState(false);
  const { setSimpleToasts } = useNotifications()!;
  const [combinedSteps, setCombinedSteps] = useState<CombinedRegistrationStep[]>([]);

  const [checkUserDetails, setCheckUserDetails] = useState(false);
  const [checkIdCheck, setCheckIdCheck] = useState(false);
  const [checkAgreement, setCheckAgreement] = useState(false);
  const [checkSafetyDeposit, setCheckSafetyDeposit] = useState(false);
  const [paymentWarning, setPaymentWarning] = useState(false);
  const [stripeCustomerId, setStripeCustomerId] = useState('');
  const [guestFormData, setGuestFormData] = useState<FormData | null>(null);
  const [accessCode, setAccessCode] = useState<string>('');

  const [showConfirmStepsModal, setShowConfirmStepsModal] = useState(false);
  const [showStepErrorModal, setShowStepErrorModal] = useState(false);
  const [continueSubmit, setContinueSubmit] = useState(false);
  const [loadingSafetyDeposit, setSafetyDeposit] = useState(false);
  const [isSkippable, setIsSkippable] = useState(false);

  const [hasHandledAccessCode, setHasHandledAccessCode] = useState(false);

  const handleSafetyDepositFlow = () => {
    const status = reservation?.InternalReservation?.safetyDepositPaymentStatus;

    if (!status) {
      setSafetyDepositWarningModal(true);
    } else if (status !== PaymentStatus.ERROR) {
      setPaymentWarning(true);
    } else {
      setSafetyDepositWarningModal(true);
    }
  };

  const modalsByTagId = [
    {
      tagId: 'user-details',
      modalState: setUserDetailsModal,
      checkState: setCheckUserDetails,
    },
    {
      tagId: 'id-upload',
      modalState: setIdCheckModal,
      checkState: setCheckIdCheck,
    },
    {
      tagId: 'agreement',
      modalState: setAgreementModal,
      checkState: setCheckAgreement,
    },
    {
      tagId: 'safety_deposit',
      modalState: handleSafetyDepositFlow,
      checkState: setCheckSafetyDeposit,
    },
  ];

  const [howDidYouHearAboutUsOptions, setHowDidYouHearAboutUsOptions] = useState<
    Option[]
  >([]);

  const { token, generateAuthToken } = useGuestAuth()!;
  const { reservationId } = useParams();
  const { startTransaction, endTransaction } = useTransaction();
  const {
    reservation,
    error: reservationError,
    setGuestName,
    getGuest,
    guestData: guest,
    cardInfo,
    getCardInfo,
    cardInfoLoading,
    dynamicQuestionsData: dynamicQuestions,
  } = useContext(GuestPortalContext)!;

  const propertyStepsUrl = `${endpoints.PROPERTIES}/${reservation?.InternalReservation?.property}/registration-steps`;
  const [{ data: propertyRegistrationStepResult }, getPropertySteps] = useRequest<
    PropertyRegistrationStep[]
  >(
    propertyStepsUrl,
    'get',
    {
      authGuestToken: token,
      propertyId: reservation?.InternalReservation?.property,
    },
    { manual: true },
  );

  const location = useLocation();
  const { state } = location;

  const [{ loading: loadingGuest, error: guestError, data: guestData }, insertGuest] =
    useMutationRequest<GuestsRequest>(
      endpoints.GUESTS,
      'post',
      {
        authGuestToken: token,
        propertyId: reservation?.InternalReservation?.property,
      },
      { manual: true },
    );

  const {
    data: howDidYouHearAboutUsOptionsData,
    loading: howDidYouHearAboutUsOptionsLoading,
    error: howDidYouHearAboutUsOptionsError,
  } = useQueryRequest<Option[]>(`${endpoints.OPTIONS}/how-do-you-hear-about-us`, {
    authGuestToken: token,
    propertyId: reservation?.InternalReservation?.property,
  });

  const url = `${endpoints.RESERVATION}/${reservationId}/registration-steps`;
  const [
    { data: reservationRegistrationStepResult, loading: loadingRegistrationSteps },
    getRegistrationSteps,
  ] = useRequest<ReservationRegistrationStep[]>(url, 'get', {
    authGuestToken: token,
    propertyId: reservation?.InternalReservation?.property,
  });

  const createDynamicAnswersUrl = buildRequestUrl(endpoints.DYNAMIC_ANSWERS, {
    propertyId: reservation?.InternalReservation?.property || '',
    reservationId: reservation?.Id || '',
  });

  const [
    {
      data: createQuestionResult,
      loading: loadingCreateQuestion,
      error: errorCreateQuestion,
    },
    createOrUpdateDynamicAnswer,
  ] = useMutationRequest<DynamicQuestionPayload[]>(
    createDynamicAnswersUrl,
    'patch',
    {
      authGuestToken: token,
    },
    {
      manual: true,
    },
  );

  const [
    {
      loading: loadingUserDetailStep,
      error: errorUserDetailsStep,
      data: userDetailsStepData,
    },
    collectGuestDetails,
  ] = useMutationRequest<Guests>(
    endpoints.USER_DETAILS_STEP,
    'post',
    {
      authGuestToken: token,
      propertyId: reservation?.InternalReservation?.property,
    },
    { manual: true },
  );

  const [
    { loading: loadingIdUploadStep, error: errorIdUploadStep, data: idUploadStepData },
    collectIdUpload,
  ] = useMutationRequest<Guests>(
    endpoints.ID_UPLOAD_STEP,
    'post',
    {
      authGuestToken: token,
      propertyId: reservation?.InternalReservation?.property,
    },
    { manual: true },
  );

  const [
    { loading: loadingAgreement, error: errorAgreement, data: agreementData },
    collectAgreement,
  ] = useMutationRequest<Guests>(
    endpoints.AGREEMENT_STEP,
    'post',
    {
      authGuestToken: token,
      propertyId: reservation?.InternalReservation?.property,
    },
    { manual: true },
  );
  const [
    { data: confirmResult, error: errorConfirmSteps, loading: loadingConfirmSteps },
    confirmGuestRegistration,
  ] = useMutationRequest<string>(
    endpoints.CONFIRM_REGISTRATION_STEP,
    'post',
    {
      authGuestToken: token,
      propertyId: reservation?.InternalReservation?.property,
    },
    { manual: true },
  );

  useEffect(() => {
    if (!howDidYouHearAboutUsOptionsData) return;

    const retrievedOptions = howDidYouHearAboutUsOptionsData.result.map((option) => ({
      name: option.name,
    }));

    setHowDidYouHearAboutUsOptions(retrievedOptions);
  }, [howDidYouHearAboutUsOptionsData]);

  const [
    { data: accessCodeResult, loading: loadingAccessCode },
    getReservationAccessCode,
  ] = useGetAccessCode(reservationId || '');

  const reservationURL = `${endpoints.MEWS_RESERVATIONS}/${reservationId}/details`;

  const [{ data: reservationGuest, loading: loadingPreregisterd }, getReservation] =
    useManualQueryRequest<Reservation>(
      reservationURL,

      {
        authGuestToken: token,
      },
      { manual: true },
    );

  const methods = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      birthDate: '',
      estimatedTimeOfArrival: '',
      howDidYouHearAboutUs: '',
      licensePlate: '',
      files: [] as File[],
      signature: '',
      dynamicQuestions: [] as DynamicQuestionPayload[],
    },
    mode: 'onChange',
  });

  const assignModals = (tagId: string) => {
    const currentStep = modalsByTagId.find((step) => step.tagId === tagId);

    return currentStep?.modalState;
  };

  const onSubmit = () => {
    const safetyStep = combinedSteps.find((step) => step.tagId === 'safety_deposit');

    setIsSkippable(!!safetyStep?.isSkippable);

    if (safetyStep && safetyStep.isSkippable === false && !continueSubmit) {
      setShowConfirmStepsModal(true);

      return;
    }

    startTransaction('confirm-registration');
    registrationLogger.info(
      { ...getTransactionContext(), reservationId },
      'Confirm registration initiated',
    );
    confirmGuestRegistration({
      reservationId,
      guestId: guest?.uuid || '',
    });

    setContinueSubmit(false);
  };

  useEffect(() => {
    if (token) {
      getReservation();
      getGuest();
      return;
    }

    generateAuthToken({ reservationId });
  }, [navigate, reservationId, token]);

  const handleCustomerId = (customerId: string, clientSecret: string) => {
    setStripeCustomerId(customerId);
  };

  const sendUserDetails = () => {
    startTransaction('submit-user-details');
    registrationLogger.info(
      { ...getTransactionContext(), reservationId },
      'User details submitted',
    );
    const allValues = methods.getValues();

    const {
      firstName,
      lastName,
      email,
      phoneNumber,
      estimatedTimeOfArrival,
      howDidYouHearAboutUs,
      licensePlate,
      birthDate,
      dynamicQuestions: dynamicQuestionPayload,
    } = allValues;

    const date = DateTime.fromJSDate(new Date(birthDate || ''));
    const formattedDate = date.toFormat('yyyy-MM-dd');

    if (config.dynamicQuestions.showQuestions) {
      collectGuestDetails({
        firstName,
        lastName,
        email,
        phoneNumber,

        reservationId,
        birthDate: formattedDate,
        guestId: guest?.uuid,
      });

      const payload = dynamicQuestionPayload
        .map((q, i) => ({
          questionId: dynamicQuestions[i].uuid,
          answer: q.answer,
        }))
        .filter((item) => item.answer && item.answer.trim() !== '');

      createOrUpdateDynamicAnswer(payload);
    } else {
      collectGuestDetails({
        firstName,
        lastName,
        email,
        phoneNumber,
        estimatedTimeOfArrival,
        howDidYouHearAboutUs,
        licensePlate,
        reservationId,
        birthDate: formattedDate,
        guestId: guest?.uuid,
      });
    }
  };

  const sendIdCheck = () => {
    startTransaction('upload-id');
    registrationLogger.info(
      { ...getTransactionContext(), reservationId },
      'ID upload submitted',
    );
    const allValues = methods.getValues();
    const { files }: { files: File[] } = allValues;

    const formData = new FormData();

    formData.append('guestId', guest?.uuid || '');
    formData.append('reservationId', reservationId || '');

    if (files) {
      files.forEach((file) => {
        const fileName = file.name;
        formData.append('files', file, fileName);
      });
    }

    collectIdUpload(formData);
  };

  const updateSafetyDepositState = () => {
    const delayMs = Number(process.env.REACT_APP_STRIPE_DELAY_MS) || 2000;
    setSafetyDeposit(true);
    setTimeout(() => {
      setCheckSafetyDeposit(true);
      setSafetyDeposit(false);
      const packagesList = state as [];
      if (!cardInfo?.last4 || packagesList.length > 0) {
        getCardInfo({ customerId: stripeCustomerId });
      }
    }, delayMs);
  };

  const sendAgreementModal = () => {
    startTransaction('submit-agreement');
    registrationLogger.info(
      { ...getTransactionContext(), reservationId },
      'Agreement submitted',
    );
    const allValues = methods.getValues();
    const { signature } = allValues;

    collectAgreement({
      signature,
      reservationId,
      guestId: guest?.uuid || '',
    });
  };

  useEffect(() => {
    if (!userDetailsStepData) return;

    registrationLogger.info(
      { ...getTransactionContext(), reservationId },
      'User details succeeded',
    );
    endTransaction();
    setUserDetailsModal(false);
    getRegistrationSteps();
    getGuest();
  }, [userDetailsStepData]);

  useEffect(() => {
    if (!idUploadStepData) return;

    registrationLogger.info(
      { ...getTransactionContext(), reservationId },
      'ID upload succeeded',
    );
    endTransaction();
    setIdCheckModal(false);
    getRegistrationSteps();
  }, [idUploadStepData]);

  useEffect(() => {
    if (!agreementData) return;

    registrationLogger.info(
      { ...getTransactionContext(), reservationId },
      'Agreement succeeded',
    );
    endTransaction();
    setAgreementModal(false);
    getRegistrationSteps();
  }, [agreementData]);

  useEffect(() => {
    if (!guest) return;

    const {
      firstName,
      lastName,
      phoneNumber,
      signature,
      email,
      birthDate,
      licensePlate,
      howDidYouHearAboutUs,
      estimatedTimeOfArrival,
    } = guest;

    let formattedBirthDate = '';
    if (birthDate) {
      const [year, month, day] = birthDate.split('T')[0].split('-');
      formattedBirthDate = `${month}/${day}/${year}`;
    }

    let filesIds: (File | string)[] = [];

    if (guest?.frontIdCard && guest?.backIdCard) {
      filesIds = [guest.frontIdCard, guest.backIdCard];
    }

    const resetValues: Record<string, unknown> = {
      firstName,
      lastName,
      phoneNumber,
      birthDate: formattedBirthDate,
      estimatedTimeOfArrival,
      howDidYouHearAboutUs,
      licensePlate,
      dynamicQuestions,
    };

    // Only include email if user details step is complete
    // Email from OTAs (Booking.com, etc.) is a proxy/masked email
    // We need the guest to manually enter their real email for invoices
    if (checkUserDetails) {
      resetValues.email = email;
    }

    if (checkIdCheck) {
      resetValues.files = filesIds;
    }

    if (checkAgreement) {
      resetValues.signature = signature;
    }

    if (dynamicQuestions.length) {
      resetValues.dynamicQuestions = dynamicQuestions.map((q) => ({
        ...q,
      }));
    }

    methods.reset(resetValues);
  }, [guest, checkUserDetails, checkIdCheck, checkAgreement]);

  useEffect(() => {
    if (!reservationGuest?.result?.stripeId) return;

    setStripeCustomerId(reservationGuest.result.stripeId);
  }, [reservationGuest?.result?.stripeId]);

  useEffect(() => {
    if (!safetyDepositModal && !agreementModal && !idCheckModal) return;

    if (!checkUserDetails) {
      setSimpleToasts({
        message: 'First write your information by clicking on Your details card',
        type: 'warning',
        show: true,
      });
      setSafetyDepositModal(false);
    }
  }, [
    checkUserDetails,
    safetyDepositModal,
    agreementModal,
    idCheckModal,
    setSimpleToasts,
  ]);

  useEffect(() => {
    if (stripeCustomerId && !cardInfo) {
      getCardInfo({ customerId: stripeCustomerId });
    }
  }, [stripeCustomerId]);

  useEffect(() => {
    if (!accessCodeResult || loadingGuest || guestError) return;
    if (hasHandledAccessCode) return;

    const packagesList = state as [];

    setAccessCode(accessCodeResult?.result || '');

    if (packagesList.length === 0 || cardInfo?.last4) {
      navigate(`/guest-portal/${reservationId}`, {
        state: {
          roomCode: accessCodeResult.result,
          packages: state || [],
        },
      });
      setHasHandledAccessCode(true);
    } else if (!cardInfo?.last4) {
      setShowAnotherCardModal(true);
      setHasHandledAccessCode(true);
    }
  }, [
    accessCodeResult,
    getReservationAccessCode,
    guestError,
    loadingGuest,
    navigate,
    reservationId,
    hasHandledAccessCode,
  ]);

  useEffect(() => {
    if (!checkUserDetails) return;

    const guestName = `${methods.getValues('firstName')} ${methods.getValues(
      'lastName',
    )}`;

    setGuestName(guestName);
  }, [checkUserDetails]);

  useEffect(() => {
    if (!reservationError) return;
    if (
      getApiErrorMessage(reservationError as AxiosError<ErrorEnvelop>) ===
      GuestPortalErrors.JWT_EXPIRED
    ) {
      generateAuthToken({ reservationId });
    }
  }, [navigate, reservationId, reservationError]);

  useEffect(() => {
    if (!reservationGuest) return;
    if (reservationGuest?.result.safetyDepositPaymentStatus === PaymentStatus.PAID) {
      setCheckSafetyDeposit(true);
    }
  }, [reservationGuest]);

  useEffect(() => {
    if (reservation?.InternalReservation) {
      if (
        reservation?.InternalReservation.safetyDepositPaymentStatus === PaymentStatus.PAID
      ) {
        setCheckSafetyDeposit(true);
      }
      getPropertySteps();
    }
  }, [reservation]);

  useEffect(() => {
    if (!reservationRegistrationStepResult || !propertyRegistrationStepResult) return;

    if (combinedSteps.length === 0) {
      const merged = mergeRegistrationSteps(
        reservationRegistrationStepResult.result ?? [],
        propertyRegistrationStepResult.result ?? [],
      );

      setCombinedSteps(merged);
      return;
    }

    const updatedCombinedSteps = combinedSteps.map((step) => {
      const backendStep = reservationRegistrationStepResult.result.find(
        (r) => r.reservationRegistrationStepId === step.reservationRegistrationStepId,
      );

      if (backendStep) {
        return {
          ...step,
          isCompleted: backendStep.isCompleted,
        };
      }
      return step;
    });

    setCombinedSteps(updatedCombinedSteps);
  }, [reservationRegistrationStepResult, propertyRegistrationStepResult]);

  useEffect(() => {
    if (!combinedSteps.length) return;

    modalsByTagId.forEach((modal) => {
      const step = combinedSteps.find((stepValue) => stepValue.tagId === modal.tagId);

      if (step) {
        modal.checkState(step.isCompleted);
      }
    });
  }, [combinedSteps]);

  useEffect(() => {
    if (!confirmResult?.result.includes('successfully')) return;

    registrationLogger.info(
      { ...getTransactionContext(), reservationId },
      'Confirm registration succeeded',
    );
    endTransaction();
    getReservationAccessCode();
    setGuestFormData(null);
  }, [confirmResult]);

  useEffect(() => {
    if (!token || !guestFormData || !guestError) return;

    if (
      getApiErrorMessage(guestError as AxiosError<ErrorEnvelop>).includes('jwt expired')
    ) {
      insertGuest({
        data: guestFormData,
        headers: {
          'Content-Type': 'multipart/form-data',
          'reservation-token': token,
        },
      });
    }
  }, [token]);

  useEffect(() => {
    if (!guestError) return;

    const errorMessage = getApiErrorMessage(guestError as AxiosError<ErrorEnvelop>);

    if (errorMessage === GuestPortalErrors.JWT_EXPIRED) {
      setSimpleToasts({
        message: 'Token expired. Please wait while we refresh it.',
        type: 'warning',
        show: true,
      });

      generateAuthToken({ reservationId });
    } else {
      setSimpleToasts({
        message: errorMessage,
        type: 'danger',
        show: true,
      });
    }
  }, [guestError, setSimpleToasts]);

  useEffect(() => {
    if (errorConfirmSteps) {
      const message = getApiErrorMessage(errorConfirmSteps as AxiosError<ErrorEnvelop>);
      registrationLogger.error(
        { ...getTransactionContext(), reservationId, error: message },
        'Confirm registration failed',
      );
      endTransaction();

      setSimpleToasts({ type: 'danger', message, show: true });

      if (message?.toLowerCase().includes('safety deposit') && !isSkippable) {
        setShowStepErrorModal(true);
      }
    }

    if (errorUserDetailsStep) {
      const message = getApiErrorMessage(
        errorUserDetailsStep as AxiosError<ErrorEnvelop>,
      );
      registrationLogger.error(
        { ...getTransactionContext(), reservationId, error: message },
        'User details failed',
      );
      endTransaction();

      setSimpleToasts({ type: 'danger', message, show: true });
    }
    if (errorIdUploadStep) {
      const message = getApiErrorMessage(errorIdUploadStep as AxiosError<ErrorEnvelop>);
      registrationLogger.error(
        { ...getTransactionContext(), reservationId, error: message },
        'ID upload failed',
      );
      endTransaction();

      setSimpleToasts({ type: 'danger', message, show: true });
    }
    if (errorAgreement) {
      const message = getApiErrorMessage(errorAgreement as AxiosError<ErrorEnvelop>);
      registrationLogger.error(
        { ...getTransactionContext(), reservationId, error: message },
        'Agreement failed',
      );
      endTransaction();

      setSimpleToasts({ type: 'danger', message, show: true });
    }
    if (errorCreateQuestion) {
      const message = getApiErrorMessage(errorCreateQuestion as AxiosError<ErrorEnvelop>);
      registrationLogger.error(
        { ...getTransactionContext(), reservationId, error: message },
        'Dynamic question failed',
      );
      endTransaction();

      setSimpleToasts({ type: 'danger', message, show: true });
    }
  }, [
    errorIdUploadStep,
    errorAgreement,
    errorUserDetailsStep,
    errorConfirmSteps,
    errorCreateQuestion,
  ]);

  useEffect(() => {
    if (!reservation?.Customer || !!guest) return;

    const {
      BirthDate: customerBirthDate,
      FirstName: firstName,
      LastName: lastName,
      Phone: phoneNumber,
    } = reservation.Customer;

    let birthDate = '';
    if (customerBirthDate) {
      const [year, month, day] = customerBirthDate.split('-');
      birthDate = `${month}/${day}/${year}`;
    }

    const resetValues: Partial<FormValues> = {
      firstName,
      lastName,
      phoneNumber,
      birthDate,
    };

    if (dynamicQuestions.length) {
      resetValues.dynamicQuestions = dynamicQuestions.map((q) => ({
        ...q,
      }));
    }

    methods.reset(resetValues);
  }, [reservation, dynamicQuestions]);

  useEffect(() => {
    if (continueSubmit) {
      onSubmit();
    }
  }, [continueSubmit]);

  const isDisabled =
    loadingConfirmSteps ||
    loadingSafetyDeposit ||
    loadingAccessCode ||
    showConfirmStepsModal ||
    showStepErrorModal ||
    !checkUserDetails ||
    !checkIdCheck ||
    !checkAgreement ||
    !checkSafetyDeposit;

  useEffect(() => {
    if (!isDisabled && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const isVisible =
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right <= (window.innerWidth || document.documentElement.clientWidth);

      if (!isVisible) {
        buttonRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
      buttonRef.current.focus();
    }
  }, [isDisabled]);

  return (
    <LoadingOverlay
      active={
        loadingAccessCode ||
        loadingGuest ||
        loadingPreregisterd ||
        loadingUserDetailStep ||
        loadingRegistrationSteps ||
        loadingAgreement ||
        loadingIdUploadStep ||
        loadingConfirmSteps ||
        loadingSafetyDeposit ||
        loadingCreateQuestion ||
        cardInfoLoading
      }
      text="Wait while we get things ready."
    >
      <FormProvider {...methods}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit();
          }}
        >
          <Container
            className="container-paddings"
            style={{ marginTop: '35px' }}
            id="guest-portal"
          >
            {combinedSteps.map((step) => {
              const checked =
                step.tagId === 'safety_deposit' ? checkSafetyDeposit : step.isCompleted;

              const title =
                step.tagId === 'safety_deposit' ? (
                  <OverlayTrigger
                    overlay={<Tooltip>Remove this add-on from the list</Tooltip>}
                  >
                    <>
                      Safety Deposit - {`${reservation?.Property?.amount || 100} USD `}
                      <icons.InfoCircle
                        onClick={() => setSafetyDepositInformation(true)}
                      />
                    </>
                  </OverlayTrigger>
                ) : (
                  step.name
                );

              const disabled =
                step.tagId === 'safety_deposit' &&
                reservationGuest?.result.safetyDepositPaymentStatus === 'PAID';

              return (
                <ReservationCard
                  key={step.tagId}
                  title={title}
                  icon={
                    <Icon
                      iconName={step?.icon as keyof typeof icons}
                      size={ICON_SIZE}
                      className="card-icon"
                    />
                  }
                  showModal={assignModals(step.tagId)}
                  checked={checked}
                  disabled={disabled}
                />
              );
            })}

            <p
              style={{
                color: 'grey',
                fontSize: '14px',
              }}
            >
              By verifying, you agree to the{' '}
              <b
                className="terms-privacy-text"
                onClick={() => setTermsAndPrivacy(true)}
                role="button"
                onKeyDown={() => setTermsAndPrivacy(true)}
                tabIndex={0}
              >
                Terms & Privacy Policy{' '}
              </b>
            </p>

            <Container
              style={{
                width: '100%',
                paddingLeft: '0px',
                paddingRight: '0px',
              }}
              onClick={(e) => {
                if (isDisabled) {
                  e.preventDefault();
                  setSimpleToasts({
                    message:
                      'Please be sure to complete all registration steps in order to be able to verify yourself.',
                    type: 'warning',
                    show: true,
                  });
                }
              }}
            >
              <Button
                ref={buttonRef}
                type="submit"
                className="save-button verify-me-button"
                disabled={isDisabled}
              >
                Verify Me
              </Button>
            </Container>

            <UserDetailsModal
              show={userDetailsModal}
              isEnd={sendUserDetails}
              handleClose={() => {
                setUserDetailsModal(false);
              }}
              howDidYouHearAboutUsOptions={howDidYouHearAboutUsOptions}
              minimalAge={
                reservation?.Property?.minimalAge
                  ? Number(reservation?.Property?.minimalAge)
                  : undefined
              }
            />
            {guest && checkUserDetails && (
              <IdCheckModal
                show={idCheckModal}
                isEnd={sendIdCheck}
                handleClose={() => {
                  setIdCheckModal(false);
                }}
              />
            )}
            {guest && checkUserDetails && (
              <AgreementModal
                show={agreementModal}
                content={reservation?.Property?.agreementContent}
                isEnd={sendAgreementModal}
                handleClose={() => {
                  setAgreementModal(false);
                }}
              />
            )}
            {reservation &&
            reservation.AssignedResourceId &&
            checkUserDetails &&
            reservationId &&
            methods.getValues('email') &&
            token ? (
              <SafetyDepositModal
                show={safetyDepositModal}
                isEnd={updateSafetyDepositState}
                handleClose={() => {
                  setSafetyDepositModal(false);
                }}
                roomTypeId={reservation.AssignedResourceId}
                reservationId={reservationId}
                handleCustomerId={handleCustomerId}
              />
            ) : null}
            <SafetyDepositInformation
              show={safetyDepositInformation}
              handleClose={() => {
                setSafetyDepositInformation(false);
              }}
            />

            <TermsAndPrivacy
              show={termsAndPrivacy}
              content={reservation?.Property?.termsContent}
              handleClose={() => {
                setTermsAndPrivacy(false);
              }}
            />
          </Container>
        </form>
      </FormProvider>
      {stripeCustomerId && accessCode && (
        <CardForPayPackagesModal
          show={showAnotherCardModal}
          handleClose={() => {
            setShowAnotherCardModal(false);
            navigate(`/guest-portal/${reservationId}`, {
              state: {
                roomCode: accessCode,
                customerId: stripeCustomerId,
                packages: state || [],
              },
            });
          }}
          customerId={stripeCustomerId}
          reservationId={reservationGuest?.result?.externalId || ''}
          accessCode={accessCode}
          packages={state}
        />
      )}
      {reservation &&
      reservation.AssignedResourceId &&
      checkUserDetails &&
      reservationId &&
      methods.getValues('email') &&
      token ? (
        <ConfirmStepsWarningModal
          show={safetyDepositWarningModal}
          handleClose={() => {
            setSafetyDepositWarningModal(false);
            setSafetyDepositModal(true);
          }}
          text={`You are authorizing this card to be used for any damages found during your stay at ${
            reservation?.Property?.name || 'The Coach House'
          }. Please note that the card is pre-authorized to ensure there are available funds during the verification process, but will not be charged unless damage is to be found upon check-out. If damage is to be found, you will be notified, including photos, prior to the card being charged- per our policy. If no damage is to be found, your deposit will be released by the end of the day of your check-out date.`}
          title="Safety Deposit"
          buttonText="I understand"
        />
      ) : null}

      <ConfirmStepsWarningModal
        show={paymentWarning}
        handleClose={() => {
          setPaymentWarning(false);
          setSafetyDepositWarningModal(true);
        }}
        text="Your payment is already being processed. Please do not enter your card details again. If the page seems stuck, wait a moment, your payment will be confirmed shortly."
        title="Safety Deposit Processing Notice"
        buttonText="I understand"
      />

      <ConfirmStepsWarningModal
        show={showConfirmStepsModal}
        handleClose={() => {
          setShowConfirmStepsModal(false);
          setContinueSubmit(true);
        }}
        text="To complete your verification, the safety deposit payment must be processed first.
        If there's a delay, wait about 5 minutes and try again.
        Still having trouble and you're sure the payment went through? Just reach out—we're happy to help!"
        title="Safety Deposit"
      />

      <ConfirmStepsWarningModal
        show={showStepErrorModal}
        handleClose={() => {
          setShowStepErrorModal(false);
        }}
        text="Your security deposit is currently pending processing, please wait 5 minutes and try to confirm again."
        title="Safety Deposit"
        icon={<icons.ExclamationOctagonFill size={25} color="red" />}
      />
    </LoadingOverlay>
  );
};
